import { useState, useEffect, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { checkActivationStatus, activateUser } from '@/api/user.api';
import { useToast } from '@/contexts/toast-context';
import { useAuth } from '@/hooks/use-auth';
import { TOAST_TYPES } from '@/constants/common.constants';
import { signIn } from '../../../utils/firebase';
import { Role } from '../../../constants/roles.constants';
import { UserProfile } from '@/hooks/use-auth';
import { usePwaInstall } from '@/hooks/use-pwa-install';

import { Card, CardContent, Button, Label, Input } from '@/components/ui';
import { Spinner, AuthFooterLinks, PwaInstallModal } from '../../../components';

import './user-activate-style.scss';

export const Route = createFileRoute('/user-activate/$uuid/')({
  component: UserActivate,
});

function UserActivate() {
  const { uuid } = Route.useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { isAuthenticated, setIsAuthenticated, setUser, setSkipFetch } =
    useAuth();
  const pwa = usePwaInstall();

  const [userEmail, setUserEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPwaModal, setShowPwaModal] = useState(false);
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});

  const isFetchingRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate requests (e.g., in StrictMode)
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    const fetchActivationStatus = async () => {
      try {
        setIsLoadingStatus(true);
        const response = await checkActivationStatus(uuid);
        setUserEmail(response.email);
      } catch (err: unknown) {
        addToast(
          err instanceof Error
            ? err.message
            : 'Failed to check activation status',
          { type: TOAST_TYPES.ERROR },
        );
        navigate({ to: '/login' });
      } finally {
        setIsLoadingStatus(false);
      }
    };
    if (!isAuthenticated) {
      fetchActivationStatus();
    } else {
      navigate({ to: '/login' });
    }
  }, [uuid, addToast, navigate]);

  const handleSubmit = async () => {
    // Validate password fields
    const newErrors: typeof errors = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (confirmPassword.length < 8) {
      newErrors.confirmPassword = 'Password must be at least 8 characters';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Submit activation
    try {
      setIsSubmitting(true);
      const response = await activateUser(uuid, password);
      const userData: UserProfile = {
        ...response.user,
        role: response.user.role as Role, // ensure correct type
      };
      setUser(userData);

      // Skip the onAuthStateChange fetch since we handle it explicitly
      setSkipFetch(true);

      // Sign in with Firebase using the email from the activation response
      await signIn(response.user.email, password);
      setIsAuthenticated(true);

      addToast('Your account is activated. Welcome to Qluu!', {
        type: TOAST_TYPES.SUCCESS,
      });

      // Show PWA install for drivers on mobile (not already installed)
      const isDriver = response.user.role === 'driver';
      if (isDriver && pwa.showInstallPrompt) {
        setShowPwaModal(true);
      } else {
        navigate({ to: '/login' });
      }
    } catch (err: unknown) {
      setIsSubmitting(false);
      addToast(
        err instanceof Error ? err.message : 'Failed to activate account',
        { type: TOAST_TYPES.ERROR },
      );
    }
  };

  const handlePwaModalClose = () => {
    setShowPwaModal(false);
    navigate({ to: '/login' });
  };

  return (
    <div className="user-activate-container">
      <div className="user-activate-content">
        <Card className="user-activate-card">
          <CardContent>
            {isLoadingStatus ? (
              <div className="user-activate-loading">
                <Spinner size="medium" />
                <p>Checking account information...</p>
              </div>
            ) : (
              <>
                <div className="user-activate-header">
                  <img
                    src="/full-logo.svg"
                    alt="Logo"
                    className="user-activate-logo"
                  />
                  <h1>Activate Your Account</h1>
                  <p className="user-activate-welcome">
                    Create a password for
                    <br />
                    <span className="user-activate-email">{userEmail}</span>
                  </p>
                </div>

                {/* Password */}
                <div className="user-activate-field">
                  <Label>Password</Label>
                  <div className="user-activate-password-wrapper">
                    <Input
                      value={password}
                      type={showPassword ? 'text' : 'password'}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) {
                          setErrors((prev) => ({
                            ...prev,
                            password: undefined,
                          }));
                        }
                      }}
                      onBlur={() => {
                        if (!password) {
                          setErrors((prev) => ({
                            ...prev,
                            password: 'Password is required',
                          }));
                        } else if (password.length < 8) {
                          setErrors((prev) => ({
                            ...prev,
                            password: 'Password must be at least 8 characters',
                          }));
                        }
                        if (
                          confirmPassword &&
                          password === confirmPassword &&
                          errors.confirmPassword === 'Passwords do not match'
                        ) {
                          setErrors((prev) => ({
                            ...prev,
                            confirmPassword: undefined,
                          }));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSubmit();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="basic-primary user-activate-password-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="invalid-feedback">{errors.password}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="user-activate-field">
                  <Label>Confirm Password</Label>
                  <div className="user-activate-password-wrapper">
                    <Input
                      value={confirmPassword}
                      type={showConfirmPassword ? 'text' : 'password'}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (errors.confirmPassword) {
                          setErrors((prev) => ({
                            ...prev,
                            confirmPassword: undefined,
                          }));
                        }
                      }}
                      onBlur={() => {
                        if (!confirmPassword) {
                          setErrors((prev) => ({
                            ...prev,
                            confirmPassword: 'Confirm password is required',
                          }));
                        } else if (confirmPassword.length < 8) {
                          setErrors((prev) => ({
                            ...prev,
                            confirmPassword:
                              'Password must be at least 8 characters',
                          }));
                        } else if (password !== confirmPassword) {
                          setErrors((prev) => ({
                            ...prev,
                            confirmPassword: 'Passwords do not match',
                          }));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSubmit();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="basic-primary user-activate-password-toggle"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      aria-label="Toggle confirm password visibility"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="invalid-feedback">{errors.confirmPassword}</p>
                  )}
                </div>

                <p className="help-text">
                  Note: Password must be a minimum of 8 characters
                </p>

                <Button
                  variant="basic-primary"
                  className="user-activate-submit-button"
                  size="medium"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Spinner size="small" />}
                  Activate Account
                </Button>
              </>
            )}
          </CardContent>
        </Card>
        <AuthFooterLinks />

        {/* PWA Install Modal for drivers */}
        <PwaInstallModal
          isOpen={showPwaModal}
          onClose={handlePwaModalClose}
          isIOS={pwa.isIOS}
          canInstall={pwa.canInstall}
          onInstall={pwa.triggerInstall}
        />
      </div>
    </div>
  );
}
